import { Routes } from '@angular/router';
import { ShellComponent } from './layout/shell/shell.component';
import { LoginComponent } from './features/auth/login.component';
import { DashboardComponent } from './features/dashboard/dashboard.component';
import { StoresComponent } from './features/stores/stores.component';
import { ProductsComponent } from './features/products/products.component';
import { OrdersComponent } from './features/orders/orders.component';
import { AnalyticsComponent } from './features/analytics/analytics.component';
import { SettingsComponent } from './features/settings/settings.component';
import { EmployeesComponent } from './features/employees/employees.component';
import { PosComponent } from './features/pos/pos.component';
import { WriteOffComponent } from './features/write-off/write-off.component';
import { CategoriesComponent } from './features/categories/categories.component';
import { VariantAttributesComponent } from './features/variant-attributes/variant-attributes.component';
import { AttendanceComponent } from './features/attendance/attendance.component';
import { DebtorsComponent } from './features/debtors/debtors.component';
import { ReportsComponent } from './features/reports/reports.component';
import { CheckInComponent } from './features/check-in/check-in.component';
import { SalesComponent } from './features/sales/sales.component';
import { SuppliersComponent } from './features/suppliers/suppliers.component';
import { PurchasesComponent } from './features/purchases/purchases.component';
import { CashComponent } from './features/cash/cash.component';
import { FinanceComponent } from './features/finance/finance.component';
import { authGuard } from './core/guards/auth.guard';
import { adminGuard, superAdminGuard } from './core/guards/admin.guard';
import { AdminShellComponent } from './features/admin-panel/admin-shell.component';
import { AdminDashboardComponent } from './features/admin-panel/admin-dashboard.component';
import { AdminCustomersComponent } from './features/admin-panel/customers/customers.component';
import { AdminStaffComponent } from './features/admin-panel/staff/staff.component';
import { AdminAnalyticsComponent } from './features/admin-panel/analytics/admin-analytics.component';
import { SubscriptionPlansComponent } from './features/admin-panel/subscription-plans/subscription-plans.component';
import { businessTenantGuard } from './core/guards/business-tenant.guard';
import { permissionGuard } from './core/guards/permission.guard';
import { SelectBusinessComponent } from './features/auth/select-business.component';
import { AccessDeniedComponent } from './features/auth/access-denied.component';
import { HomeComponent } from './features/home/home.component';
import { LeadApplicationsComponent } from './features/admin-panel/lead-applications/lead-applications.component';
import { AdminAnnouncementsComponent } from './features/admin-panel/announcements/announcements.component';
import { AdminStoreTypesComponent } from './features/admin-panel/store-types/store-types.component';
import { AdminSmsGatewayComponent } from './features/admin-panel/sms-gateway/sms-gateway.component';
import { AdminAppReleasesComponent } from './features/admin-panel/app-releases/app-releases.component';
import { AdminNotificationTemplatesComponent } from './features/admin-panel/notification-templates/notification-templates.component';
import { AdminFeedbacksComponent } from './features/admin-panel/feedbacks/feedbacks.component';

export const routes: Routes = [
  { path: '', component: HomeComponent },
  { path: 'login', component: LoginComponent },
  {
    path: 'select-business',
    component: SelectBusinessComponent,
    canActivate: [authGuard],
  },
  {
    path: 'access-denied',
    component: AccessDeniedComponent,
    canActivate: [authGuard],
  },
  { path: 'check-in', component: CheckInComponent },

  {
    path: 'admin',
    component: AdminShellComponent,
    canActivate: [adminGuard],
    children: [
      { path: '', component: AdminDashboardComponent, pathMatch: 'full' },
      { path: 'customers', component: AdminCustomersComponent },
      { path: 'lead-applications', component: LeadApplicationsComponent },
      { path: 'feedbacks', component: AdminFeedbacksComponent },
      { path: 'analytics', component: AdminAnalyticsComponent },
      { path: 'staff', component: AdminStaffComponent, canActivate: [superAdminGuard] },
      {
        path: 'subscription-plans',
        component: SubscriptionPlansComponent,
        canActivate: [superAdminGuard],
      },
      {
        path: 'announcements',
        component: AdminAnnouncementsComponent,
        canActivate: [superAdminGuard],
      },
      {
        path: 'store-types',
        component: AdminStoreTypesComponent,
        canActivate: [superAdminGuard],
      },
      {
        path: 'notification-templates',
        component: AdminNotificationTemplatesComponent,
        canActivate: [superAdminGuard],
      },
      {
        path: 'app-releases',
        component: AdminAppReleasesComponent,
        canActivate: [superAdminGuard],
      },
      {
        path: 'sms-gateway',
        component: AdminSmsGatewayComponent,
        canActivate: [superAdminGuard],
      },
    ],
  },

  {
    path: 'app',
    component: ShellComponent,
    canActivate: [authGuard, businessTenantGuard],
    children: [
      {
        path: '',
        component: DashboardComponent,
        canActivate: [permissionGuard],
        data: { permissions: ['DASHBOARD_VIEW'], forbidSystemRoles: true },
      },
      {
        path: 'stores',
        component: StoresComponent,
        canActivate: [permissionGuard],
        data: { permissions: ['STORE_VIEW'], forbidSystemRoles: true },
      },
      {
        path: 'pos/:storeId',
        component: PosComponent,
        canActivate: [permissionGuard],
        data: {
          permissions: ['ORDER_VIEW', 'ORDER_CREATE'],
          forbidSystemRoles: true,
        },
      },
      {
        path: 'write-off',
        component: WriteOffComponent,
        canActivate: [permissionGuard],
        data: { permissions: ['INVENTORY_WRITEOFF'], forbidSystemRoles: true },
      },
      {
        path: 'products',
        component: ProductsComponent,
        canActivate: [permissionGuard],
        data: { permissions: ['PRODUCT_VIEW'], forbidSystemRoles: true },
      },
      {
        path: 'orders',
        component: OrdersComponent,
        canActivate: [permissionGuard],
        data: { permissions: ['ORDER_VIEW'], forbidSystemRoles: true },
      },
      {
        path: 'sales',
        component: SalesComponent,
        canActivate: [permissionGuard],
        data: { permissions: ['ORDER_CREATE'], forbidSystemRoles: true },
      },
      {
        path: 'analytics',
        component: AnalyticsComponent,
        canActivate: [permissionGuard],
        data: { permissions: ['INVENTORY_VIEW'], forbidSystemRoles: true },
      },
      {
        path: 'categories',
        component: CategoriesComponent,
        canActivate: [permissionGuard],
        data: { permissions: ['CATEGORY_VIEW'], forbidSystemRoles: true },
      },
      {
        path: 'variant-attributes',
        component: VariantAttributesComponent,
        canActivate: [permissionGuard],
        data: { permissions: ['PRODUCT_VIEW'], forbidSystemRoles: true },
      },
      {
        path: 'employees',
        component: EmployeesComponent,
        canActivate: [permissionGuard],
        data: { customerOnly: true, forbidSystemRoles: true },
      },
      {
        path: 'attendance',
        component: AttendanceComponent,
        canActivate: [permissionGuard],
        data: {
          permissions: ['ATTENDANCE_VIEW', 'DASHBOARD_VIEW'],
          forbidSystemRoles: true,
          requiredModule: 'attendance',
        },
      },
      {
        path: 'debtors',
        component: DebtorsComponent,
        canActivate: [permissionGuard],
        data: { permissions: ['ORDER_VIEW'], forbidSystemRoles: true },
      },
      {
        path: 'suppliers',
        component: SuppliersComponent,
        canActivate: [permissionGuard],
        data: { permissions: ['SUPPLIER_VIEW'], forbidSystemRoles: true },
      },
      {
        path: 'purchases',
        component: PurchasesComponent,
        canActivate: [permissionGuard],
        data: { permissions: ['PURCHASE_VIEW'], forbidSystemRoles: true },
      },
      {
        path: 'cash',
        component: CashComponent,
        canActivate: [permissionGuard],
        data: { permissions: ['FINANCE_VIEW'], forbidSystemRoles: true },
      },
      {
        path: 'finance',
        component: FinanceComponent,
        canActivate: [permissionGuard],
        data: { permissions: ['FINANCE_VIEW'], forbidSystemRoles: true },
      },
      {
        path: 'reports',
        component: ReportsComponent,
        canActivate: [permissionGuard],
        data: { permissions: ['REPORT_VIEW'], forbidSystemRoles: true },
      },
      { path: 'settings', component: SettingsComponent },
    ],
  },

  { path: '**', redirectTo: '' },
];
